import React, { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { ProviderIcon } from "../../../shared/ProviderIcon";
import { PROVIDER_ORDER, toPayloadProvider } from "../providerMap";
import { ContextCardLink, InfoHint, SectionLabel } from "./shared";

const VISIBLE_COUNT = 5;

function normalizeBrandKey(value) {
	return String(value || "")
		.trim()
		.toLowerCase()
		.replace(/^www\./, "");
}

function isOwnBrand(competitor, targetBrand) {
	if (!competitor) return false;
	if (
		competitor.is_target_brand ||
		competitor.is_target ||
		competitor.is_own ||
		competitor.is_own_brand
	) {
		return true;
	}
	const name = normalizeBrandKey(competitor.name);
	const domain = normalizeBrandKey(competitor.domain);
	const targetName = normalizeBrandKey(targetBrand?.name);
	const targetDomain = normalizeBrandKey(targetBrand?.domain);
	if (targetName && name === targetName) return true;
	if (targetDomain && (domain === targetDomain || name === targetDomain)) return true;
	return false;
}

function CompetitorTable({
	competitors,
	equalColumns = false,
	highlightNames = new Set(),
}) {
	const tableClassName = [
		"monitoring__analysis-strategy-competitor-table",
		equalColumns ? "monitoring__competitors-table--equal" : "",
	]
		.filter(Boolean)
		.join(" ");

	return (
		<div className="table-responsive">
			<table className={tableClassName}>
				<thead>
					<tr>
						<th>Agency</th>
						{PROVIDER_ORDER.map((p) => (
							<th key={p} className="monitoring__competitors-col-equal text-center">
								<ProviderIcon provider={p} size={14} className="mx-auto" />
							</th>
						))}
						<th className="monitoring__competitors-col-equal text-center">Mentions</th>
					</tr>
				</thead>
				<tbody>
					{competitors.map((c) => {
						const highlight = highlightNames.has(c.name);
						return (
							<tr
								key={c.name}
								className={
									highlight
										? "monitoring__analysis-strategy-competitor-row--own"
										: undefined
								}
							>
								<td className="monitoring__competitors-col-agency">
									<span className="monitoring__analysis-strategy-competitor-name">
										{c.name}
									</span>
									{highlight ? (
										<span className="monitoring__analysis-strategy-competitor-you">
											You
										</span>
									) : null}
								</td>
								{PROVIDER_ORDER.map((p) => {
									const presenceMap = c.provider_presence ?? {};
									const presence =
										presenceMap[toPayloadProvider(p)] ?? presenceMap[p];
									return (
										<td key={p} className="monitoring__competitors-col-equal text-center">
											{presence?.mentioned ? (
												presence.position ? (
													<strong>#{presence.position}</strong>
												) : (
													<Check
														size={14}
														className="monitoring__analysis-strategy-competitor-check"
														aria-label="Mentioned"
													/>
												)
											) : (
												<span className="text-muted">—</span>
											)}
										</td>
									);
								})}
								<td className="monitoring__competitors-col-equal text-center">
									{c.mention_count}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

export default function CompetitorConsensusCard({
	strategy,
	brandName,
	brandDomain,
	embedded = false,
}) {
	const [allOpen, setAllOpen] = useState(false);
	const targetBrand = useMemo(
		() => ({
			name: brandName || strategy.target_brand?.name,
			domain: brandDomain || strategy.target_brand?.domain,
		}),
		[brandName, brandDomain, strategy.target_brand]
	);
	const sortedCompetitors = useMemo(
		() => [...strategy.competitors].sort((a, b) => b.mention_count - a.mention_count),
		[strategy.competitors]
	);
	const highlightNames = useMemo(() => {
		const top = sortedCompetitors[0];
		if (!top || !isOwnBrand(top, targetBrand)) return new Set();
		const topCount = top.mention_count;
		return new Set(
			sortedCompetitors
				.filter(
					(competitor) =>
						competitor.mention_count === topCount &&
						isOwnBrand(competitor, targetBrand)
				)
				.map((competitor) => competitor.name)
		);
	}, [sortedCompetitors, targetBrand]);
	const shownCompetitors = sortedCompetitors.slice(0, VISIBLE_COUNT);
	const hasMore = sortedCompetitors.length > shownCompetitors.length;

	const body = (
		<>
			{embedded && (
				<p className="small text-muted mb-2">
					How often competitors appear across AI provider responses for this prompt.
				</p>
			)}
			{shownCompetitors.length > 0 ? (
				<CompetitorTable
					competitors={shownCompetitors}
					highlightNames={highlightNames}
				/>
			) : (
				<p className="small text-muted mb-0">No competitors identified.</p>
			)}

			{hasMore && shownCompetitors.length > 0 && (
				<ContextCardLink onClick={() => setAllOpen(true)}>
					View all competitors
				</ContextCardLink>
			)}
		</>
	);

	return (
		<>
			{embedded ? (
				body
			) : (
				<div className="monitoring__analysis-strategy-card monitoring__analysis-strategy-row-card monitoring__analysis-strategy-row-card--competitors monitoring__analysis-strategy-context-card">
					<div className="monitoring__analysis-strategy-card-head">
						<div className="d-flex align-items-center gap-1">
							<SectionLabel>Competitor Consensus</SectionLabel>
							<InfoHint text="How often competitors appear across AI provider responses for this prompt." />
						</div>
					</div>
					<div className="monitoring__analysis-strategy-card-body">{body}</div>
				</div>
			)}

			<Modal
				isOpen={allOpen}
				toggle={() => setAllOpen(false)}
				size="lg"
				className="monitoring__competitors-modal"
				centered
			>
				<ModalHeader toggle={() => setAllOpen(false)} tag="div">
					<span className="monitoring__writing-modal-title">All competitors</span>
					<span className="monitoring__writing-modal-desc">
						{sortedCompetitors.length} agencies mentioned across providers
					</span>
				</ModalHeader>
				<ModalBody className="monitoring__competitors-modal-body">
					<CompetitorTable
						competitors={sortedCompetitors}
						equalColumns
						highlightNames={highlightNames}
					/>
				</ModalBody>
			</Modal>
		</>
	);
}
